import type { Language } from '@eformulieren/engine'
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { useSnapshot } from '../document/snapshot.js'
import { inLanguage } from '../language/text.js'
import { type PresenceSource, type Present, counted, othersHere, presenceColor, presentName, presentUsersIn, worksHereText } from './PresenceBar.js'

/**
 * Wie er nu in dit veld werkt (R-72, R-139, verzoek van de productowner van 3 november 2026).
 *
 * Bij de sectie staat al "X werkt hier" (`PresenceBar`, G-18), maar in een grote sectie zegt dat niet
 * in welk veld. Hier staat het bij het veld zelf, met dezelfde naam als bij de sectie (`presentName`
 * met dezelfde `nameOf`) en dezelfde woorden: "werkt hier" en niet "typt hier", want de awareness zegt
 * waar iemand de focus heeft en niet of hij typt.
 *
 * **De bron is `veld` in de awareness van de sectie.** De schil zet daar de id van het veld met de
 * focus (`FillForm`), en de pod laat alleen de id van een veld van deze sectie door
 * (`apps/runtime/src/boundAwareness.ts`). De naam komt nooit uit `veld` maar uit `user`, en dus uit
 * het token.
 *
 * **Op de bovenrand van het invoervak, zonder dat er iets verspringt.** Het label zweeft in een vak
 * zonder hoogte direct boven de control (`.ef-veld__aanwezig` in schil.css), zoals het label van de
 * sectie op de bovenrand van de kaart. Wie in hetzelfde veld typt, ziet zijn invoer niet wegschuiven
 * als een ander binnenkomt.
 *
 * **Kleur is nooit de enige drager** (R-139): de stip heeft de kleur van de cursor van die persoon, en
 * de naam staat er als tekst bij.
 *
 * **Een beleefde melding, alleen voor het veld waar de lezer zelf is.** Elke beweging van een cursor is
 * een wijziging in de awareness; een live region op elk label zou bij elke toetsaanslag van een ander
 * spreken, en over velden waar de lezer niet is. Daarom één melding als er iemand bij komt in of weggaat
 * uit het veld waar de lezer de focus heeft, en verder niets. Wie met de Tab-toets in een veld komt,
 * hoort het label als beschrijving van de control (`aria-describedby` in `FieldShell`).
 */

export interface FieldPresenceProps {
  readonly awareness: PresenceSource
  /** De id van het veld, zoals de schil hem als `veld` in de awareness zet. */
  readonly veldId: string
  /** Het id van het label, waar de control met `aria-describedby` naar wijst. */
  readonly id: string
  readonly taal: Language
  /** Hoe een ander met een persoonssleutel heet; dezelfde functie als bij de sectie. */
  readonly nameOf?: (person: string) => string
  /** De eigen persoonssleutel: een tweede venster van de lezer is geen ander (G-18). */
  readonly ik?: string
}

/** Wie anders dan de lezer in dit veld werkt, met zijn naam zoals de sectie hem noemt en zijn kleur. */
export function othersInField(
  present: readonly Present[],
  fieldId: string,
  language: Language,
  nameOf?: (person: string) => string,
  me?: string,
): readonly { readonly naam: string; readonly kleur: string }[] {
  return othersHere(present, (one) => one.focus && one.veld === fieldId, me).map((one) => ({
    naam: presentName(one, language, nameOf),
    kleur: presenceColor(one),
  }))
}

const JOINED = { nl: 'werkt nu ook in dit veld.', en: 'is now also working in this field.' }
const LEFT = { nl: 'werkt niet meer in dit veld.', en: 'is no longer working in this field.' }

export function FieldPresence({ awareness, veldId: fieldId, id, taal: language, nameOf, ik: me }: FieldPresenceProps): ReactNode {
  const subscribe = useCallback(
    (hook: () => void) => {
      awareness.on('change', hook)
      return () => awareness.off('change', hook)
    },
    [awareness],
  )
  // `useSnapshot` vergelijkt op inhoud: een cursor die beweegt verandert deze lijst niet, en dan
  // hertekent het label ook niet.
  const read = useCallback(
    () => othersInField(presentUsersIn(awareness, language), fieldId, language, nameOf, me),
    [awareness, fieldId, language, nameOf, me],
  )
  const here = useSnapshot(subscribe, read)
  const names = counted(here.map((one) => one.naam))

  const boxRef = useRef<HTMLDivElement>(null)
  const [announcement, setAnnouncement] = useState('')
  const previous = useRef<readonly string[] | undefined>(undefined)
  const namesKey = names.join('\u0001')
  useEffect(() => {
    const now = namesKey === '' ? [] : namesKey.split('\u0001')
    const before = previous.current
    previous.current = now
    // De beginstand wordt niet gemeld, en een veld waar de lezer niet is ook niet.
    if (before === undefined) return
    const field = boxRef.current?.closest('.ef-veld')
    const active = document.activeElement
    if (field === null || field === undefined || active === null || !field.contains(active)) return
    const came = now.filter((name) => !before.includes(name))
    const went = before.filter((name) => !now.includes(name))
    const said = [
      ...came.map((name) => `${name} ${inLanguage(JOINED, language)}`),
      ...went.map((name) => `${name} ${inLanguage(LEFT, language)}`),
    ]
    if (said.length > 0) setAnnouncement(said.join(' '))
  }, [namesKey, language])

  return (
    <div className="ef-veld__aanwezig" ref={boxRef}>
      {/* Er altijd, ook leeg: `aria-describedby` wijst ernaar, en een verwijzing naar een id dat er niet is, is een fout. */}
      <p className="ef-presence ef-presence--veld" id={id} hidden={here.length === 0} data-proef="veld-aanwezig">
        <span className="ef-presence__stippen" aria-hidden="true">
          {here.map((one, index) => (
            <span key={index} className="ef-presence__stip" style={{ '--ef-presencekleur': one.kleur } as Record<string, string>} />
          ))}
        </span>
        <span className="ef-presence__tekst">{names.length === 0 ? '' : worksHereText(names, language)}</span>
      </p>
      {/* De regio staat er altijd en op dezelfde plek: een live region die er met inhoud en al bij komt, leest een schermlezer niet voor. */}
      <span className="ef-sr-only" aria-live="polite" data-proef="veld-aanwezig-melding">
        {announcement}
      </span>
    </div>
  )
}
